import axios from "../utils/http/axios";
import { ServerName } from "../utils/constants/serverName";
import { ChangePasswordDTO, CreateUserDTO, ResetPasswordDTO, UserOverview } from "../models/user";
import router from "../router";
import { showNotification } from "../service/notification";
import { getI18n } from "../i18n";

/**
 * Self-service update of the current user's `properties` bag via the dedicated
 * `PUT /user/me/properties` endpoint. The `patch` is DEEP-MERGED server-side into the
 * existing properties (objects merge recursively, `null` removes a key, arrays/primitives
 * replace), so callers send only the changed subset. Constraints enforced by the backend:
 * object only, ≤ 64 KB, nesting depth ≤ 16. Returns the merged properties bag.
 */
export const updateMyProperties = async (patch: Record<string, unknown>): Promise<Record<string, unknown>> => {
  const response = await axios.put<Record<string, unknown>>(`${ServerName}/user/me/properties`, patch);
  return response.data;
};

export const activateSudo = async (username: string, password: string) => {
  try {
    await axios.post<string>(`${ServerName}/user/sudo`, {
      username,
      password,
    });
    return Promise.resolve();
  } catch (e: any) {
    return Promise.reject(e);
  }
};
export const login = async (username: string, password: string) => {
  try {
    const url = `${ServerName}/authenticate`;

    const result = await axios.post<string>(url, { username, password });
    return Promise.resolve(result.data);
  } catch (error: any) {
    if (error.response) {
      console.error("Error Response Data:", error.response.data);
      console.error("Error Status:", error.response.status);
    } else if (error.request) {
      console.error("No response received (network issue?):", error.request);
    } else {
      console.error("Unexpected Error:", error.message);
    }

    return Promise.reject("Die Anmeldedaten sind leider ungültig");
  }
};

export const loginSSO = async (username: string, app = "") => {
  try {
    let url = `${ServerName}/sso`;
    if (app.length > 0) {
      url = `${url}?app=${app}`;
    }

    const result = await axios.post<{ sso_url: string }>(url, { username });
    return Promise.resolve(result.data.sso_url);
  } catch (error: any) {
    console.log(error);
    if (error.response) {
      console.error("Error Response Data:", error.response.data);
      console.error("Error Status:", error.response.status);
      if (error.response.status === 404) {
        showNotification("error", "SSO ist für diesen User nicht aktiviert");
      } else {
        showNotification("error", "Anmeldung fehlgeschlagen");
      }
    } else if (error.request) {
      console.error("No response received (network issue?):", error.request);
      showNotification("error", "Anmeldung fehlgeschlagen");
    } else {
      console.error("Unexpected Error:", error.message);
      showNotification("error", "Anmeldung fehlgeschlagen");
    }

    return Promise.reject(error);
  }
};

export const addUser = async (user: CreateUserDTO) => {
  return axios
    .post(`${ServerName}/user`, user)
    .then((result) => {
      return Promise.resolve();
    })
    .catch((error) => {
      return Promise.reject();
    });
};
export const updateUser = async (user: UserOverview) => {
  delete user["authorities"];
  return axios
    .put(`${ServerName}/user`, user)
    .then((result) => {
      return Promise.resolve();
    })
    .catch((error) => {
      return Promise.reject();
    });
};

export const deleteUser = async (userId: string) => {
  return axios
    .delete(`${ServerName}/user/${userId}`)
    .then((response) => {
      return Promise.resolve();
    })
    .catch((error) => {
      return Promise.reject(error);
    });
};

export const resetPassword = async (userId: string) => {
  return axios
    .delete(`${ServerName}/user/password`, { data: userId })
    .then((response) => {
      return Promise.resolve();
    })
    .catch((error) => {
      return Promise.reject(error);
    });
};

export const getAllUsers = async (tenantId: number) => {
  const params = new URLSearchParams();
  params.append("tenantid", tenantId);
  return axios
    .get<UserOverview[]>(`${ServerName}/user`, {
      params: params,
    })
    .then((response) => {
      return Promise.resolve(response.data);
    })
    .catch((error) => {
      return Promise.reject(error);
    });
};

export const changePassword = async (data: ResetPasswordDTO | ChangePasswordDTO) => {
  const t = getI18n().global.t;

  try {
    const response = await axios.put(ServerName + "/user/password", data);
    showNotification("success", t("changepassword.success"));
    router.push({ name: "Large Language Model", force: true });
  } catch {
    showNotification("error", t("changepassword.error"));
  }
};
